import { INGREDIENT_NAMES } from './ingredients'

/**
 * One round of snake — the rules only, no DOM.
 *
 * "Free flow": the snake wraps around the screen edges and only dies when it
 * bites itself. Three kinds of food:
 *
 * - the squares of the word, where the word stood when the round started:
 *   a quarter point each;
 * - one pizza ingredient at a time, from the start: one point. It covers a
 *   2×2 block of cells between the header and footer bar (never across a
 *   screen edge, and outside the word while any of it is left) and is eaten
 *   as a whole as soon as the head enters any of its cells; the next one
 *   appears right away. A chili also makes the snake hot for a while: twice
 *   as fast, drawn red and orange;
 * - now and then a pineapple next to it, the easter egg: placed like an
 *   ingredient, gone again after 10–20 s. Eating it halves the score, the
 *   snake doesn't grow and turns sick green for a while.
 *
 * Effects and the pineapple run on the round's own clock, `round.time`,
 * which only moves on through advance() — the game loop stops calling it
 * while the game is paused, so a pause costs no effect time.
 *
 * The score is counted in whole quarters, so the fractions never drift;
 * `round.score` is the same in points.
 */
export const DIRECTIONS = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

/** Quarter points per food; the pineapple halves the score instead. */
export const QUARTERS = { word: 1, ingredient: 4 }

/** Cells per side of an ingredient. */
export const INGREDIENT_CELLS = 2

export const PINEAPPLE = 'pineapple'
export const CHILI = 'chili'

/** The easter egg is never part of the regular rotation. */
export const REGULAR_INGREDIENTS = INGREDIENT_NAMES.filter((name) => name !== PINEAPPLE)

/** Game time an effect lasts, in ms; eating the same food again starts it afresh. */
export const EFFECT_MS = { hot: 20_000, sick: 15_000 }

/** How much faster a hot snake moves. */
export const HOT_SPEEDUP = 2

// The pineapple: a chance with every new regular ingredient, never in the
// first seconds of a round, and only one at a time.
export const PINEAPPLE_CHANCE = 0.2
export const PINEAPPLE_GRACE_MS = 5_000
export const PINEAPPLE_MIN_MS = 10_000
export const PINEAPPLE_MAX_MS = 20_000
// It blinks through its last seconds, so players see it is about to go.
export const PINEAPPLE_BLINK_MS = 2_000
const PINEAPPLE_BLINK_STEP_MS = 200
// Cells kept clear ahead of the head: a pineapple popping up right in the
// snake's path could not be dodged.
const PINEAPPLE_HEAD_GAP = 4

const START_LENGTH = 4
const MAX_QUEUED_TURNS = 2
const SPAWN_ATTEMPTS = 64
// Cells kept clear around the word, so an ingredient never touches the logo.
const WORD_MARGIN = 1

// Steps per second: ≈9 at the start, a little faster with every point.
// The highscore endpoint relies on the cap: ≥ 50 ms per step (≥ 25 ms while
// hot), at most one point per step.
const BASE_SPEED = 9
const MAX_SPEED = 20
const SPEED_PER_POINT = 0.05

const keyOf = (x, y) => `${x},${y}`
const wrap = (value, size) => ((value % size) + size) % size

/** @param {number} score  points */
export const stepInterval = (score) =>
  1000 / Math.min(MAX_SPEED, BASE_SPEED + score * SPEED_PER_POINT)

/** @param {'hot'|'sick'} kind */
export const hasEffect = (round, kind) =>
  round.effects.some((effect) => effect.kind === kind && effect.until > round.time)

/** The time a step takes right now: the score's pace, halved while hot. */
export const intervalOf = (round) =>
  stepInterval(round.score) / (hasEffect(round, 'hot') ? HOT_SPEEDUP : 1)

/**
 * How the snake looks: the latest effect started that is still running, or
 * null for the plain white snake.
 * @returns {'hot'|'sick'|null}
 */
export function snakeLook(round) {
  for (let i = round.effects.length - 1; i >= 0; i--) {
    if (round.effects[i].until > round.time) return round.effects[i].kind
  }
  return null
}

/** The pineapple is in its last seconds. */
export const pineappleFading = (round) =>
  round.pineapple !== null && round.pineapple.until - round.time <= PINEAPPLE_BLINK_MS

/** Whether the pineapple shows right now — while it fades, only every other blink step. */
export function pineappleShown(round) {
  if (!round.pineapple) return false
  if (!pineappleFading(round)) return true
  return Math.floor((round.pineapple.until - round.time) / PINEAPPLE_BLINK_STEP_MS) % 2 === 1
}

/** The cells a block with its top-left corner at x, y covers. */
function* blockCells(x, y) {
  for (let dy = 0; dy < INGREDIENT_CELLS; dy++) {
    for (let dx = 0; dx < INGREDIENT_CELLS; dx++) yield { x: x + dx, y: y + dy }
  }
}

const covers = (block, x, y) =>
  x >= block.x && x < block.x + INGREDIENT_CELLS && y >= block.y && y < block.y + INGREDIENT_CELLS

const overlapsBlock = (block, x, y) =>
  Math.abs(block.x - x) < INGREDIENT_CELLS && Math.abs(block.y - y) < INGREDIENT_CELLS

/** Any regular ingredient but the previous one. */
function pickIngredient(previous) {
  const names = REGULAR_INGREDIENTS.filter((name) => name !== previous)
  const pool = names.length ? names : REGULAR_INGREDIENTS
  return pool[Math.floor(Math.random() * pool.length)]
}

/**
 * The area ingredients stay out of while squares of the word are left: its
 * bounding box grown by WORD_MARGIN. The squares alone would not do — the
 * word's strokes are one cell thin, so a 2×2 block fits into the counters of
 * "d" and "o" and under the arches of the "n"s without touching a square.
 * @returns {{left: number, top: number, right: number, bottom: number}|null}  inclusive cells
 */
function wordBox(word) {
  if (!word.size) return null
  let left = Infinity
  let top = Infinity
  let right = -Infinity
  let bottom = -Infinity
  for (const { x, y } of word.values()) {
    left = Math.min(left, x)
    top = Math.min(top, y)
    right = Math.max(right, x)
    bottom = Math.max(bottom, y)
  }
  return {
    left: left - WORD_MARGIN,
    top: top - WORD_MARGIN,
    right: right + WORD_MARGIN,
    bottom: bottom + WORD_MARGIN,
  }
}

const overlapsBox = (box, x, y) =>
  x + INGREDIENT_CELLS > box.left &&
  x <= box.right &&
  y + INGREDIENT_CELLS > box.top &&
  y <= box.bottom

/**
 * A 2×2 block at x, y is on the grid (not across an edge), clear of the snake,
 * the word and the food already there, and — given a `keepOut` box — outside
 * that box.
 */
function fits(round, x, y, keepOut = null) {
  const { grid, occupied, word } = round
  if (x < 0 || y < 0 || x + INGREDIENT_CELLS > grid.cols || y + INGREDIENT_CELLS > grid.rows) {
    return false
  }
  if (keepOut && overlapsBox(keepOut, x, y)) return false
  for (const food of [round.ingredient, round.pineapple]) {
    if (food && overlapsBlock(food, x, y)) return false
  }
  for (const cell of blockCells(x, y)) {
    const key = keyOf(cell.x, cell.y)
    if (occupied.has(key) || word.has(key)) return false
  }
  return true
}

/**
 * The rows a block's top-left corner may take so that the whole block sits
 * between the bars; on a screen too low for that, every row.
 */
function spawnRows(grid) {
  const last = grid.safeRowEnd - INGREDIENT_CELLS
  return last >= grid.safeRowStart
    ? { first: grid.safeRowStart, last }
    : { first: 0, last: grid.rows - INGREDIENT_CELLS }
}

const inSpawnRows = (grid, y) => {
  const { first, last } = spawnRows(grid)
  return y >= first && y <= last
}

/**
 * A random free spot between the bars and away from the word; on a crowded
 * screen the best free spot there is.
 */
function findSpot(round) {
  const { grid } = round
  const safe = spawnRows(grid)
  const all = { first: 0, last: grid.rows - INGREDIENT_CELLS }
  const lastX = grid.cols - INGREDIENT_CELLS
  const box = wordBox(round.word)

  if (lastX >= 0 && safe.last >= safe.first) {
    for (let attempt = 0; attempt < SPAWN_ATTEMPTS; attempt++) {
      const x = Math.floor(Math.random() * (lastX + 1))
      const y = safe.first + Math.floor(Math.random() * (safe.last - safe.first + 1))
      if (fits(round, x, y, box)) return { x, y }
    }
  }

  // Few spots left: collect them all. Hidden under a bar is worse than inside
  // the word, so between the bars comes first, then away from the word.
  const rowRanges = safe.first === all.first && safe.last === all.last ? [safe] : [safe, all]
  const keepOuts = box ? [box, null] : [null]
  for (const { first, last } of rowRanges) {
    for (const keepOut of keepOuts) {
      const spots = []
      for (let y = first; y <= last; y++) {
        for (let x = 0; x <= lastX; x++) {
          if (fits(round, x, y, keepOut)) spots.push({ x, y })
        }
      }
      if (spots.length) return spots[Math.floor(Math.random() * spots.length)]
    }
  }

  return null
}

/**
 * Put a regular ingredient on the screen — a random one unless `name` is
 * given. Where there is no room left, there is none until the snake moves on.
 */
function spawnIngredient(round, name = pickIngredient(round.previousIngredient)) {
  round.ingredient = null
  const spot = findSpot(round)
  if (!spot) return
  round.ingredient = { ...spot, name }
  round.previousIngredient = name
}

/** Cells between the head and a block, around the screen edges where that is shorter. */
function distanceFromHead(round, block) {
  const head = round.body[0]
  if (!head) return Infinity
  const along = (from, to, size) => {
    let best = Infinity
    for (let d = 0; d < INGREDIENT_CELLS; d++) {
      const gap = wrap(to + d - from, size)
      best = Math.min(best, gap, size - gap)
    }
    return best
  }
  return Math.max(along(head.x, block.x, round.grid.cols), along(head.y, block.y, round.grid.rows))
}

/** Now and then a pineapple next to a new ingredient, if there is none yet. */
function maybeSpawnPineapple(round) {
  if (round.pineapple || round.time < PINEAPPLE_GRACE_MS) return
  if (Math.random() >= PINEAPPLE_CHANCE) return

  const spot = findSpot(round)
  if (!spot || distanceFromHead(round, spot) < PINEAPPLE_HEAD_GAP) return

  const lifetime = PINEAPPLE_MIN_MS + Math.random() * (PINEAPPLE_MAX_MS - PINEAPPLE_MIN_MS)
  round.pineapple = { ...spot, name: PINEAPPLE, until: round.time + lifetime }
}

/** Start (or restart) an effect; the latest one started colours the snake. */
function startEffect(round, kind) {
  round.effects = [
    ...round.effects.filter((effect) => effect.kind !== kind),
    { kind, until: round.time + EFFECT_MS[kind] },
  ]
}

/**
 * @param {object} grid       see layout.js
 * @param {Array<{x: number, y: number}>} wordCells  the placed word, a quarter point per square
 * @param {keyof DIRECTIONS} directionName  initial heading
 */
export function createRound(grid, wordCells, directionName = 'up') {
  const direction = DIRECTIONS[directionName] ?? DIRECTIONS.up
  const word = new Map(wordCells.map(({ x, y }) => [keyOf(x, y), { x, y }]))

  // Start between the word and the bar the snake comes from, heading for it.
  const rows = wordCells.map((cell) => cell.y)
  const wordTop = rows.length ? Math.min(...rows) : Math.floor(grid.rows / 2)
  const wordBottom = rows.length ? Math.max(...rows) : wordTop
  const startX = Math.floor(grid.cols / 2)
  const startY =
    direction.y > 0
      ? Math.floor((grid.safeRowStart + wordTop) / 2)
      : Math.floor((wordBottom + grid.safeRowEnd) / 2)

  const body = []
  const occupied = new Set()
  for (let i = 0; i < START_LENGTH; i++) {
    const x = wrap(startX - direction.x * i, grid.cols)
    const y = wrap(startY - direction.y * i, grid.rows)
    const key = keyOf(x, y)
    if (occupied.has(key)) break
    body.push({ x, y })
    occupied.add(key)
    word.delete(key)
  }

  const round = {
    grid,
    body,
    occupied,
    word,
    ingredient: null,
    previousIngredient: null,
    /** @type {{x: number, y: number, name: string, until: number}|null} */
    pineapple: null,
    /** @type {Array<{kind: 'hot'|'sick', until: number}>}  oldest first */
    effects: [],
    /** Game time in ms, see advance(). */
    time: 0,
    direction,
    queue: [],
    quarters: 0,
    score: 0,
  }
  spawnIngredient(round)
  return round
}

/**
 * Let game time pass. The caller's clock stops while the game is paused, so
 * the pineapple and the effects wait with it.
 * @param {number} ms
 * @returns {boolean}  something visible changed: the pineapple went, an effect ended
 */
export function advance(round, ms) {
  if (!(ms > 0)) return false
  round.time += ms

  let changed = false
  if (round.pineapple && round.pineapple.until <= round.time) {
    round.pineapple = null
    changed = true
  }
  const effects = round.effects.filter((effect) => effect.until > round.time)
  if (effects.length !== round.effects.length) {
    round.effects = effects
    changed = true
  }
  return changed
}

/**
 * Queue a change of heading (two at most, so quick double turns register
 * within one step). Reversing into the neck is ignored.
 */
export function turn(round, directionName) {
  const next = DIRECTIONS[directionName]
  if (!next || round.queue.length >= MAX_QUEUED_TURNS) return false

  const last = round.queue.at(-1) ?? round.direction
  if (next === last || (next.x === -last.x && next.y === -last.y)) return false

  round.queue.push(next)
  return true
}

/**
 * Move one cell.
 * @returns {{
 *   died: boolean,
 *   food: {kind: 'word'|'ingredient'|'pineapple', x: number, y: number, name?: string}|null,
 *   effect: 'hot'|'sick'|null,
 * }}  `effect` is the one the food just started
 */
export function step(round) {
  const { grid, body, occupied, word, ingredient, pineapple } = round
  round.direction = round.queue.shift() ?? round.direction

  const head = body[0]
  const x = wrap(head.x + round.direction.x, grid.cols)
  const y = wrap(head.y + round.direction.y, grid.rows)
  const key = keyOf(x, y)

  let food = null
  if (ingredient && covers(ingredient, x, y)) food = { kind: 'ingredient', ...ingredient }
  else if (pineapple && covers(pineapple, x, y)) food = { kind: 'pineapple', ...pineapple }
  else if (word.has(key)) food = { kind: 'word', x, y }

  // The snake grows by what it eats — except the pineapple. Without growth
  // the tail moves on in the same step, so the head may take its cell.
  const tail = food && food.kind !== 'pineapple' ? null : body.pop()
  if (tail) occupied.delete(keyOf(tail.x, tail.y))

  if (occupied.has(key)) {
    if (tail) {
      body.push(tail)
      occupied.add(keyOf(tail.x, tail.y))
    }
    // The round is over: no effect carries on.
    round.effects = []
    return { died: true, food: null, effect: null }
  }

  body.unshift({ x, y })
  occupied.add(key)

  let effect = null
  if (food?.kind === 'word') {
    word.delete(key)
    round.quarters += QUARTERS.word
  } else if (food?.kind === 'ingredient') {
    round.quarters += QUARTERS.ingredient
    if (food.name === CHILI) effect = 'hot'
  } else if (food?.kind === 'pineapple') {
    // Halved, down to whole quarters: 12.75 → 6.25, 0.25 → 0.
    round.quarters = Math.floor(round.quarters / 2)
    round.pineapple = null
    effect = 'sick'
  }
  round.score = round.quarters / 4
  if (effect) startEffect(round, effect)

  // The next ingredient right away (or, on a full screen, as soon as there
  // is room) — and with it, now and then, a pineapple.
  if (food?.kind === 'ingredient' || !round.ingredient) {
    spawnIngredient(round)
    if (round.ingredient) maybeSpawnPineapple(round)
  }

  return { died: false, food, effect }
}

/**
 * Where a food goes after a rewrap: its wrapped spot if the whole block still
 * fits there between the bars and outside the word, otherwise a new spot —
 * or nowhere, if the screen has no room left.
 */
function relocate(round, food) {
  const { grid } = round
  const x = wrap(food.x, grid.cols)
  const y = wrap(food.y, grid.rows)
  if (fits(round, x, y, wordBox(round.word)) && inSpawnRows(grid, y)) return { ...food, x, y }

  const spot = findSpot(round)
  return spot ? { ...food, ...spot } : null
}

/**
 * The screen changed size mid-round: keep the cells, wrap everything onto the
 * new grid. Where the shrunken grid folds the snake onto itself, the tail from
 * that point on is dropped rather than killing the player. Food that no longer
 * fits as a whole between the bars, or now lies in the word, moves to a new
 * spot; the pineapple keeps its remaining time.
 */
export function rewrap(round, grid) {
  round.grid = grid
  round.occupied.clear()

  const body = []
  for (const cell of round.body) {
    const x = wrap(cell.x, grid.cols)
    const y = wrap(cell.y, grid.rows)
    const key = keyOf(x, y)
    if (round.occupied.has(key)) break
    round.occupied.add(key)
    body.push({ x, y })
  }
  round.body = body

  const word = new Map()
  for (const cell of round.word.values()) {
    const x = wrap(cell.x, grid.cols)
    const y = wrap(cell.y, grid.rows)
    const key = keyOf(x, y)
    if (!round.occupied.has(key)) word.set(key, { x, y })
  }
  round.word = word

  // Placed one after the other, so neither lands on the other.
  const { ingredient, pineapple } = round
  round.ingredient = null
  round.pineapple = null
  if (ingredient) round.ingredient = relocate(round, ingredient)
  else spawnIngredient(round)
  if (pineapple) round.pineapple = relocate(round, pineapple)
}
